import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { BackButton } from "@/components/BackButton";

const FAQS = [
  {
    q: "Where do you deliver?",
    a: "We deliver within New York City only — all five boroughs (Manhattan, Brooklyn, Queens, The Bronx and Staten Island). Every order is packed, carried and placed in your home by our own team. If your ZIP code is outside NYC, leave your email at checkout and we'll notify you when we expand.",
  },
  {
    q: "How much is delivery?",
    a: "Hand-delivery arrangements are confirmed after you order — our team reaches out to set a time that suits you. Any delivery fee is shown clearly at checkout before you pay; there are never surprise charges after.",
  },
  {
    q: "How do I track my order?",
    a: "Use the Track Order page with your order number (SONY-…) and the email you checked out with. Signed-in members can also see live status under My Account → My Orders.",
  },
  {
    q: "What is member pricing?",
    a: "Members get $25 off every order over $199, automatically. Just create a free account and sign in before checkout — the discount appears in your cart.",
  },
  {
    q: "Which payment methods do you accept?",
    a: "Visa, Mastercard, American Express, Apple Pay and Google Pay — all processed securely by Stripe. We never see or store your card details.",
  },
  {
    q: "How do returns work?",
    a: "You have 30 days from delivery. Sign in, go to My Account → Returns & Refunds, pick the order and tell us what's wrong — we reply within 24 hours with return instructions. Refunds go back to your original payment method after a quick warehouse inspection. Full details on the Return Policy page.",
  },
  {
    q: "Can I sign in with my phone number?",
    a: "Yes — choose Mobile OTP on the sign-in page and we'll text you a one-time code. No password to remember.",
  },
  {
    q: "Where do your products come from?",
    a: "Our catalog is curated from vetted suppliers and fulfilled through CJ Dropshipping, so we can offer great prices for the quality without a warehouse markup.",
  },
];

export default function Help() {
  const [open, setOpen] = useState(0);
  return (
    <main data-testid="help-page" className="min-h-screen bg-bone px-6 pb-32 pt-32 sm:px-10">
      <div className="mx-auto max-w-3xl">
        <BackButton label="Back to store" />
        <p className="mb-4 mt-10 text-xs uppercase tracking-[0.3em] text-ash">Help @ FAQ</p>
        <h1 className="font-serif text-4xl font-light leading-tight tracking-tight text-charcoal sm:text-5xl">
          Questions, answered.
        </h1>
        <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-ash">
          Can't find what you need? Write to us at{" "}
          <a href="mailto:info@shoponlinenewyork.com" className="underline underline-offset-4 hover:text-charcoal">
            info@shoponlinenewyork.com
          </a>{" "}
          or use the chat bubble on any page — we reply within 48 hours.
        </p>

        <div className="mt-14 divide-y divide-charcoal/10 border-y border-charcoal/10" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <div key={i}>
              <button
                data-testid={`faq-q-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
              >
                <span className="font-serif text-lg font-light text-charcoal sm:text-xl">{f.q}</span>
                <Plus
                  size={18}
                  className={`shrink-0 text-ash transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}
                />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p data-testid={`faq-a-${i}`} className="pb-6 pr-10 text-sm font-light leading-relaxed text-ash">
                      {f.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <p className="mt-12 text-xs font-light text-ash">
          Looking for the fine print?{" "}
          <Link to="/return-policy" className="underline underline-offset-4 hover:text-charcoal">Returns</Link>,{" "}
          <Link to="/terms" className="underline underline-offset-4 hover:text-charcoal">Terms</Link> and{" "}
          <Link to="/privacy" className="underline underline-offset-4 hover:text-charcoal">Privacy</Link>.
        </p>
      </div>
    </main>
  );
}
